//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { Bookmark, BookmarkBorderOutlined } from '@material-ui/icons';
import { Link, makeStyles, Tooltip, useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core';
import VerifiedUserRounded from '@material-ui/icons/VerifiedUserRounded';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { AppContext, LayoutContext } from '../../common/contexts';
import { ANALYTICS_EVENT_JS_SAVED_JOB, MESSAGE_ONLY_JS_CAN_SAVE_JOB, VISIT_BRAND_PAGE } from '../../common/constants';
import { getCityStateText, getSalaryRateText } from '../../common/textHelper';
import { getBrandPageUrlPathByEmployerId, stopEventPropagation } from '../../common/linkHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { bookmarkJob } from '../../common/services/jobCenterServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import clsx from 'clsx';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';

import { differenceInSeconds } from 'date-fns';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    // const { isBrandPage } = params;

    return makeStyles(theme => ({
        card: {
            borderLeft: '4px solid transparent',
            display: 'flex',
            padding: '15px',
            cursor: 'pointer',
        },
        activeCard: {
            borderLeft: '4px solid #1D8A8B',
            background: 'rgba(0, 0, 0, 0.03)',
        },
        logoLinkContainer: {
            cursor: 'pointer',
        },
        companyProfilePic: {
            width: 60,
            height: 60,
            marginRight: '10px',
            border: '1px solid #D5D7DD',
            background: 'rgba(252, 130, 31, 0.1)',
            color: 'rgb(252,130,31)',
            fontSize: '24px',
        },
        middleSection: {
            flex: 1,
        },
        jobTitle: {
            display: 'block',

            fontWeight: 'bold',
            color: '#111e14',
        },
        companyNameLink: {
            textDecoration: 'underline',
        },
        companyNameContainer: {
            display: 'flex',
        },
        companyName: {
            display: 'inline-block',
            // color: '#9b9e9c',
            color: '#1D8A8B',
            textDecoration: 'underline',
        },
        verifyIcon: {
            color: '#1D8A8B',
            fontSize: '17px',
            margin: '3px 5px 0 5px',
        },
        employerCityState: {
            display: 'block',
            color: '#9b9e9c',
        },
        employerCountry: {
            display: 'block',
            color: '#9b9e9c',
        },
        salary: {
            color: '#9b9e9c',
        },
        type: {
            color: '#9b9e9c',
        },
        publishedOn: {
            fontSize: '13px',
            color: '#9b9e9c',
            display: 'block',
        },
        bookmarkIconButton: {
            marginTop: '15px',
            float: 'right', //TODO: Fix this!!
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        layoutContext,
        employerAttemptSaveJobDialogOpen,
        setEmployerAttemptSaveJobDialogOpen,
        handleClickCompanyLogo,
        jobEmployerId,
        mdMatchesUp,
        setTempBookmarkedJobs,
        setTempUnbookmarkedJobs,
    } = params;
    const {
        job,
        state,
        country,
        city,
        jobTitle,
        currency,
        currencySymbol,
        employmentType,
        // salary,
        // otherSalary,
        isExactRate,
        rateMin,
        rateMax,
        payFrequency,
        companyName,
        // rate,
        isSelected,
        onClick,
        companyProfilePicUrl,
    } = props;

    const authUser = _get(appContext, 'authUser');
    const userId = _get(appContext, 'userId');
    const employerId = _get(appContext, 'employerId');
    const isEmployer = !!employerId;

    const bookmarkedJobs = _get(appContext, 'currentUser.bookmarkedJobs', []);
    // const isBookmarked = bookmarkedJobs?.includes(props.uid); //uid is the job id

    const { isBrandPage, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const jobId = _get(props, 'uid');
    const isBookmarked = bookmarkedJobs?.includes(jobId);

    const { card: cardDefaultClass, activeCard: cardActiveClass } = classes;

    const defns = {
        card: {
            className: clsx(cardDefaultClass, { [cardActiveClass]: isSelected }),
            onClick: handleClick.bind(null, onClick),
        },

        employerLogoLink: {
            isBrandPage,
            onClick: handleClickCompanyLogo,
            profilePicUrl: companyProfilePicUrl,
            brandEmployerId: jobEmployerId,
        },
        bookmarkIconButton: {
            // isHidden: isBrandPage && isEmployer,
            isBookmarked,
            onClick: async () => {
                if (!authUser || isEmployer) {
                    setEmployerAttemptSaveJobDialogOpen(true);
                } else {
                    const isAdd = !isBookmarked;
                    const success = await bookmarkJob({ isAdd, userId, jobId, setSnackbarMessage, setSnackbarErrorMessage });
                    success && logEvent(ANALYTICS_EVENT_JS_SAVED_JOB);

                    if (success) {
                        if (isAdd) {
                            // remember the job for illusion of real-time
                            setTempBookmarkedJobs(prev => {
                                let cleanedUpList = { ...prev };
                                _forEach(_keys(prev), jobId => {
                                    if (differenceInSeconds(new Date(), prev[jobId].updateDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
                                });
                                return { ...cleanedUpList, [job.uid]: { data: job, updateDt: new Date() } };
                            });
                            // clean up the other list
                            setTempUnbookmarkedJobs(prev => _omit({ ...prev }, [job.uid]));
                        } else {
                            // remember the job Id for illusion of real-time
                            setTempUnbookmarkedJobs(prev => {
                                let cleanedUpList = { ...prev };
                                _forEach(_keys(prev), jobId => {
                                    if (differenceInSeconds(new Date(), prev[jobId].deleteDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
                                });
                                return { ...cleanedUpList, [jobId]: { id: jobId, deleteDt: new Date() } };
                            });
                            // clean up the other list
                            setTempBookmarkedJobs(prev => _omit({ ...prev }, [job.uid]));
                        }
                    }
                }
            },
        },

        jobTitle: {
            variant: 'body1',
            value: jobTitle,
        },
        companyNameLink: {
            color: 'secondary',
            title: "Visit Company's Brand Page",
            onClick: mdMatchesUp ? stopEventPropagation : handleClickCompanyLogo,
            href: mdMatchesUp ? getBrandPageUrlPathByEmployerId(jobEmployerId) : undefined,
            target: mdMatchesUp ? '_blank' : undefined,
        },
        companyName: {
            variant: 'body2',
            color: 'secondary',
            value: `${companyName}`,
            isBrandPage,
            brandEmployerId: jobEmployerId,
        },
        employerCityState: {
            variant: 'body2',
            value: getCityStateText(city, state), //getCityStateCountryText(city, state, country),
        },
        employerCountry: {
            variant: 'body2',
            value: country,
        },
        salary: {
            variant: 'body2',
            value: `${currencySymbol} ${getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency)}`,
        },
        type: {
            variant: 'body2',
            value: employmentType,
        },
        employerAttemptJsActionsDialog: {
            message: employerAttemptSaveJobDialogOpen ? MESSAGE_ONLY_JS_CAN_SAVE_JOB : null,
            open: employerAttemptSaveJobDialogOpen,
            onClose: () => {
                setEmployerAttemptSaveJobDialogOpen(false);
            },
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClick = onClick => {
    if (onClick) onClick();
};

const BookmarkIconButton = props => {
    const { isHidden, isBookmarked, onClick } = props;

    return isHidden ? null : (
        <div onClick={onClick}>
            <Tooltip title="Save to My Jobs">{isBookmarked ? <Bookmark style={{ color: '#FC821F' }} /> : <BookmarkBorderOutlined />}</Tooltip>
        </div>
    );
};

const EmployerLogoLink = props => {
    const { isBrandPage, onClick, profilePicUrl, brandEmployerId } = props;
    const classes = useStyles({ isBrandPage })();
    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    const defns = {
        logoLinkContainer: {
            onClick: isBrandPage ? undefined : onClick,
        },
        logoLink: {
            href: getBrandPageUrlPathByEmployerId(brandEmployerId),
            target: '_blank',
        },
        companyProfilePic: {
            variant: 'rounded',
            src: profilePicUrl,
        },
        tooltip: {
            title: VISIT_BRAND_PAGE,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return isBrandPage ? (
        <div {...defns.logoContainer}>
            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
        </div>
    ) : (
        <div {...defns.logoContainer}>
            <Tooltip {...defns.tooltip}>
                <div>
                    {mdMatchesUp ? (
                        <Link {...defns.logoLink}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </Link>
                    ) : (
                        <div {...defns.logoLinkContainer}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobItem = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);

    const layoutContext = useContext(LayoutContext);
    const { isBrandPage, setBrandPageDialogInfo, setTempBookmarkedJobs, setTempUnbookmarkedJobs } = layoutContext;

    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { employerId: jobEmployerId, isEmpBadge: jobIsEmpBadge } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [employerAttemptSaveJobDialogOpen, setEmployerAttemptSaveJobDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleClickCompanyLogo = evt => {
        evt.stopPropagation();
        setBrandPageDialogInfo({ open: true, employerId: jobEmployerId });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isBrandPage })();
    const defns = getDefns({
        classes,
        props,
        appContext,
        layoutContext,
        employerAttemptSaveJobDialogOpen,
        setEmployerAttemptSaveJobDialogOpen,
        handleClickCompanyLogo,
        jobEmployerId,
        mdMatchesUp,
        setTempBookmarkedJobs,
        setTempUnbookmarkedJobs,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.card}>
            <EmployerLogoLink {...defns.employerLogoLink} />
            <div {...defns.middleSection}>
                <AfitdTypography {...defns.jobTitle} />
                <div {...defns.companyNameContainer}>
                    <AfitdLink {...defns.companyNameLink}>
                        <AfitdTypography {...defns.companyName} />
                    </AfitdLink>
                    {jobIsEmpBadge && <VerifiedUserRounded {...defns.verifyIcon} />}
                </div>
                <AfitdTypography {...defns.employerCityState} />
                <AfitdTypography {...defns.employerCountry} />
                <AfitdTypography {...defns.salary} />
                <AfitdTypography {...defns.type} />
            </div>
            <div>
                <BookmarkIconButton {...defns.bookmarkIconButton} />
            </div>
            <AfitdMessageDialog {...defns.employerAttemptJsActionsDialog} />
        </div>
    );
};

export default JobItem;
